// Inline glyphs for the Reports hub — currentColor so they inherit the
// category/severity accent (the app's pattern: local inline SVGs, no icon lib).
import type { IncidentCategory } from '../../../interface/vesr';

const stroke = { stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

export const ShieldIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M12 3 5 6v5c0 4.4 3 8.4 7 9.7 4-1.3 7-5.3 7-9.7V6l-7-3Z'
			{...stroke}
		/>
		<path
			d='m9.3 11.8 1.9 1.9 3.5-3.7'
			{...stroke}
		/>
	</svg>
);

export const TruckIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M3 7h10v9H3V7Zm10 3h4l3 3v3h-7'
			{...stroke}
		/>
		<circle
			cx='7'
			cy='17.5'
			r='1.6'
			{...stroke}
		/>
		<circle
			cx='16.5'
			cy='17.5'
			r='1.6'
			{...stroke}
		/>
	</svg>
);

export const BallotIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M5 12h14l1.5 7h-17L5 12Z'
			{...stroke}
		/>
		<path
			d='M9 12V5.5h6V12'
			{...stroke}
		/>
		<path
			d='m10.7 8 1.4 1.4 2.4-2.6'
			{...stroke}
		/>
	</svg>
);

export const ChipIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<rect
			x='7'
			y='7'
			width='10'
			height='10'
			rx='2'
			{...stroke}
		/>
		<path
			d='M10 4v3m4-3v3M10 17v3m4-3v3M4 10h3m-3 4h3m10-4h3m-3 4h3'
			{...stroke}
		/>
	</svg>
);

export const FlagIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M6 21V4m0 1h11l-2.5 3.5L17 12H6'
			{...stroke}
		/>
	</svg>
);

export const DotsIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<circle
			cx='6'
			cy='12'
			r='1.4'
			fill='currentColor'
		/>
		<circle
			cx='12'
			cy='12'
			r='1.4'
			fill='currentColor'
		/>
		<circle
			cx='18'
			cy='12'
			r='1.4'
			fill='currentColor'
		/>
	</svg>
);

export const CategoryGlyph = ({ category }: { category: IncidentCategory }) => {
	switch (category) {
		case 'Security':
			return <ShieldIcon />;
		case 'Logistics':
			return <TruckIcon />;
		case 'Electoral':
			return <BallotIcon />;
		case 'Technical':
			return <ChipIcon />;
		case 'Political':
			return <FlagIcon />;
		default:
			return <DotsIcon />;
	}
};

export const PinGlyph = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
		width='18'
		height='18'
	>
		<path
			d='M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z'
			{...stroke}
		/>
		<circle
			cx='12'
			cy='10'
			r='2.5'
			{...stroke}
		/>
	</svg>
);

export const MegaphoneGlyph = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M4 9v6h3l8 4V5L7 9H4Zm13 .5a3 3 0 0 1 0 5'
			{...stroke}
		/>
	</svg>
);

export const WarnGlyph = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			{...stroke}
		/>
	</svg>
);

// Live signal beacon — a transmitting field beacon (filled core on a mast with
// radiating arcs). Used by the overview "Reports" CTA; `.beacon-core` is the
// pulse target for the CSS animation in overview/style.scss.
export const BeaconIcon = ({ size = 16 }: { size?: number }) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<circle
			className='beacon-core'
			cx='12'
			cy='8.5'
			r='1.9'
			fill='currentColor'
		/>
		<path
			d='M8.6 11.9a4.8 4.8 0 0 1 0-6.8'
			{...stroke}
		/>
		<path
			d='M15.4 5.1a4.8 4.8 0 0 1 0 6.8'
			{...stroke}
		/>
		<path
			d='M6 14.5a8.5 8.5 0 0 1 0-12'
			opacity='0.45'
			{...stroke}
		/>
		<path
			d='M18 2.5a8.5 8.5 0 0 1 0 12'
			opacity='0.45'
			{...stroke}
		/>
		<path
			d='M12 10.5V21'
			{...stroke}
		/>
	</svg>
);

export const CalmGlyph = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M12 3 5 6v5c0 4.4 3 8.4 7 9.7 4-1.3 7-5.3 7-9.7V6l-7-3Z'
			{...stroke}
		/>
		<path
			d='M8.5 12.5c1 1.2 2.2 1.8 3.5 1.8s2.5-.6 3.5-1.8'
			{...stroke}
		/>
	</svg>
);
